import { useState } from 'react';
import { Gift, Ticket } from 'lucide-react';
import { applyInviteCode, applyCampaignCode } from '@/lib/codeRedemption';

interface CodeRedemptionCardProps {
  userId: string;
  onSuccess?: () => void;
}

export const CodeRedemptionCard = ({ userId, onSuccess }: CodeRedemptionCardProps) => {
  const [inviteCode, setInviteCode] = useState('');
  const [campaignCode, setCampaignCode] = useState('');
  const [isApplyingInvite, setIsApplyingInvite] = useState(false);
  const [isApplyingCampaign, setIsApplyingCampaign] = useState(false);

  const handleApplyInviteCode = async () => {
    if (!inviteCode.trim()) {
      alert('招待コードを入力してください');
      return;
    }

    setIsApplyingInvite(true);
    const result = await applyInviteCode(userId, inviteCode.trim());
    setIsApplyingInvite(false);

    if (result.success) {
      alert(result.message);
      setInviteCode('');
      onSuccess?.();
    } else {
      alert(result.message);
    }
  };

  const handleApplyCampaignCode = async () => {
    if (!campaignCode.trim()) {
      alert('キャンペーンコードを入力してください');
      return;
    }

    setIsApplyingCampaign(true);
    const result = await applyCampaignCode(userId, campaignCode.trim());
    setIsApplyingCampaign(false);

    if (result.success) {
      alert(result.message);
      setCampaignCode('');
      onSuccess?.();
    } else {
      alert(result.message);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <h2 className="text-xl font-bold text-gray-800 mb-4">
        コードを利用
      </h2>

      {/* 招待コード入力 */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Gift className="w-5 h-5 text-blue-600" />
          <label htmlFor="inviteCode" className="text-sm font-medium text-gray-700">
            招待コード
          </label>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          友達の招待コードを入力すると、Standardプラン30日間が無料で利用できます
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            id="inviteCode"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            placeholder="例: ABC12345"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
            maxLength={8}
          />
          <button
            onClick={handleApplyInviteCode}
            disabled={isApplyingInvite || !inviteCode.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isApplyingInvite ? '適用中...' : '適用'}
          </button>
        </div>
      </div>

      {/* キャンペーンコード入力 */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Ticket className="w-5 h-5 text-purple-600" />
          <label htmlFor="campaignCode" className="text-sm font-medium text-gray-700">
            キャンペーンコード
          </label>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          キャンペーンコードを入力すると、特別なプランが適用されます
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            id="campaignCode"
            value={campaignCode}
            onChange={(e) => setCampaignCode(e.target.value.toUpperCase())}
            placeholder="例: SPRING2026"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
          />
          <button
            onClick={handleApplyCampaignCode}
            disabled={isApplyingCampaign || !campaignCode.trim()}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isApplyingCampaign ? '適用中...' : '適用'}
          </button>
        </div>
      </div>
    </div>
  );
};
